import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { resolveNavigation } from '@platform/entitlements';
import type { PortalModule } from '../../api';
import { ModulePlaceholder } from '../ModulePlaceholder';
import { SectionIndex } from '../SectionIndex';
import { shouldSkipStart } from './skipStart';
import { SpnStartPage } from './StartPage';
import { DiscoveriesLanding } from './discoveries/DiscoveriesLanding';
import { DiscoverySession } from './discoveries/DiscoverySession';
import { NewDiscovery } from './discoveries/NewDiscovery';
import { Transfer } from './discoveries/Transfer';

/**
 * Strategic Portfolio Navigator — screen resolution, the same shape APR and AI Readiness
 * use. Keyed by the section-key trail, so a route is derived from the catalogue rather
 * than written twice; a key with no entry falls through to ModulePlaceholder.
 *
 * The source has no left-nav tree at all — its Strategic Intelligence is a landing page of
 * boxed cards. This platform's menu structure applies instead (owner's ruling), so the
 * group is a drawer bucket and Discoveries is a section beneath it.
 */
const SCREENS: Record<string, () => JSX.Element> = {
  discoveries: () => <DiscoveriesLanding />,
  transfer: () => <Transfer />,
};

export interface SpnModuleProps {
  modules: PortalModule[];
  /** Greets the user, as the source's start screen does. */
  firstName?: string;
}

export function SpnModule({ modules, firstName }: SpnModuleProps) {
  const module = modules.find((m) => m.id === 'spn');
  if (!module || module.sections.length === 0) return <Navigate to="/" replace />;
  return <SpnContent modules={modules} module={module} firstName={firstName} />;
}

function SpnContent({
  modules,
  module,
  firstName,
}: {
  modules: PortalModule[];
  module: PortalModule;
  firstName?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const at = resolveNavigation(location.pathname, [module]);

  // `/spn` is the product's own Start Menu (owner's ruling, 2026-08-04) — entering the
  // module lands there, not on a list of buckets. The buckets stay in the left nav.
  if (at.trail.length === 0) {
    // Unless the user asked never to see it again. `?start=1` always wins, or the toggle
    // that turns the preference back off would be unreachable once it was on.
    const skipTo = shouldSkipStart(location.search);
    if (skipTo) return <Navigate to={skipTo} replace />;
    return <SpnStartPage firstName={firstName} sections={module.sections} />;
  }

  const deepest = at.trail[at.trail.length - 1]!;

  if (!at.section) {
    return (
      <SectionIndex
        title={deepest.label}
        sections={deepest.sections ?? []}
        onOpen={(path) => navigate(path)}
      />
    );
  }

  const key = at.trail.map((s) => s.key).join('/');

  // `/spn/discoveries/new` — a sub-path of the Discoveries screen rather than a catalogue
  // section, so `resolveNavigation` stops at Discoveries and the rest is read off the
  // path. Same pattern AI Readiness uses for a record id.
  if (key === 'discoveries' && at.section) {
    const rest = location.pathname.slice(at.section.path.length + 1).split('/').filter(Boolean);
    if (rest[0] === 'new') return <NewDiscovery />;
    // Anything else in that position is a session code — `/spn/discoveries/DSESS-…`.
    if (rest[0]) return <DiscoverySession code={rest[0]} />;
  }

  const render = SCREENS[key];
  if (!render) return (
      <ModulePlaceholder
        modules={modules}
        module={module}
        section={at.section ? { key: at.section.key, label: at.section.label } : undefined}
      />
    );
  return render();
}
